interface ZakekeMessageCommon {
    readonly zakekeMessageType: number | string;
}

interface ZakekeMessageCustomizerAddToCart extends ZakekeMessageCommon {
    readonly zakekeMessageType: 0;
    readonly designId: string;
}

interface ZakekeMessageComposerAddToCart {
    readonly zakekeMessageType: 'AddToCart';
    readonly message: {
        readonly composition: string;
        readonly preview?: string;
        readonly quantity?: number;
    };
}

interface ZakekeMessageDesignChange extends ZakekeMessageCommon {
    readonly zakekeMessageType: 1;
    readonly design?: {
        readonly color?: string | null;
    };
}

interface ZakekeMessageCustomizerProductAttributes extends ZakekeMessageCommon {
    readonly zakekeMessageType: 2;
    readonly data?: {
        readonly promiseId?: string | number | null;
    };
}

interface ZakekeMessageCustomizerProductPrice extends ZakekeMessageCommon {
    readonly zakekeMessageType: 3;
    readonly data?: {
        readonly promiseId?: string | number | null;
    };
}

export interface ZakekeAddon {
    readonly addonID: number;
    readonly billingSystemAddonID: string | null;
    readonly description: string;
    readonly name: string;
    readonly periodUnit: 'DAY' | 'MONTH' | 'WEEK' | 'YEAR';
    readonly code: string;
    readonly published: boolean;
    readonly price: number;
    readonly sellerID: number | null;
    readonly rates: ReadonlyArray<{
        readonly feeValue: number;
    }>;
}

export interface ZakekeMessageAddonsList extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'AddonsList';
    readonly addons: ReadonlyArray<ZakekeAddon>;
    readonly currency: string;
}

// Iframe -> backoffice: the Test Mode iframe is ready to receive initialization messages.
interface ZakekeMessageTestModeReady extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'TestModeReady';
}

// Discovery mode channel (mirrors the customizer's useDiscoveryModeUpdater contract).
// Iframe -> backoffice: the customizer announces it has booted in discovery mode and is listening.
interface ZakekeMessageDiscoveryModeReady extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeReady';
}

// Iframe -> backoffice: the customizer is fully loaded and ready for interaction — booted, and (if a
// variant-selection dialog was prompted) the shopper has chosen a variant so the dialog is gone. Flips
// back to false if a variant dialog reopens (e.g. an out-of-stock variant change). Drives the discovery
// tool buttons' enabled state — they stay disabled until this is true.
interface ZakekeMessageDiscoveryModeInteractive extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeInteractive';
    readonly interactive: boolean;
}

// Iframe -> backoffice: the ids of AI Creative Tools / Tools discovery pills that are already active on the
// opened product (computed from the product's real, non-discovery configuration, aggregated across all
// variants/sides). The panel hides these pills — there's nothing to "discover" about a feature the merchant
// already has. Snapshotted once the customizer is interactive; recomputed only when the product changes.
interface ZakekeMessageDiscoveryModeHiddenTools extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeHiddenTools';
    readonly hiddenTools: string[];
}

// Backoffice -> iframe: stream a feature selection (print method / pricing rule / AI / tool)
// to apply to the previewed product without reloading. Payload shape is finalized in a later step.
export interface ZakekeMessageDiscoveryModeUpdate extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeUpdate';
    readonly category: 'printing' | 'pricing' | 'ai' | 'tools' | 'quantity';
    readonly payload: unknown;
}

export type ZakekeMessage =
    | ZakekeMessageCommon
    | ZakekeMessageCustomizerAddToCart
    | ZakekeMessageComposerAddToCart
    | ZakekeMessageDesignChange
    | ZakekeMessageCustomizerProductAttributes
    | ZakekeMessageCustomizerProductPrice
    | ZakekeMessageAddonsList
    | ZakekeMessageTestModeReady
    | ZakekeMessageDiscoveryModeReady
    | ZakekeMessageDiscoveryModeInteractive
    | ZakekeMessageDiscoveryModeHiddenTools
    | ZakekeMessageDiscoveryModeUpdate;

export const postAddonsList = (iframe: HTMLIFrameElement | null, addons: ReadonlyArray<ZakekeAddon>, currency: string) => {
    const message: ZakekeMessageAddonsList = {
        zakekeMessageType: 'AddonsList',
        addons,
        currency
    };
    iframe?.contentWindow?.postMessage(message, '*');
};

export const isCustomizerAddToCartMessage = (message: any): message is ZakekeMessageCustomizerAddToCart => message?.zakekeMessageType === 0;

export const isComposerAddToCartMessage = (message: any): message is ZakekeMessageComposerAddToCart =>
    message?.zakekeMessageType === 'AddToCart' && !!message?.message?.composition;

/** @deprecated Use isCustomizerAddToCartMessage instead */
export const isAddToCartMessage = isCustomizerAddToCartMessage;

export const isDesignChangeMessage = (message: ZakekeMessage): message is ZakekeMessageDesignChange => message.zakekeMessageType === 1;

export const isCustomizerProductAttributesMessage = (message: ZakekeMessage): message is ZakekeMessageCustomizerProductAttributes =>
    message.zakekeMessageType === 2;

export const isCustomizerProductPriceMessage = (message: ZakekeMessage): message is ZakekeMessageCustomizerProductPrice => message.zakekeMessageType === 3;

export const isDiscoveryModeReadyMessage = (message: any): message is ZakekeMessageDiscoveryModeReady => message?.zakekeMessageType === 'DiscoveryModeReady';

export const isTestModeReadyMessage = (message: any): message is ZakekeMessageTestModeReady => message?.zakekeMessageType === 'TestModeReady';

export const isDiscoveryModeInteractiveMessage = (message: any): message is ZakekeMessageDiscoveryModeInteractive =>
    message?.zakekeMessageType === 'DiscoveryModeInteractive' && typeof message?.interactive === 'boolean';

export const isDiscoveryModeHiddenToolsMessage = (message: any): message is ZakekeMessageDiscoveryModeHiddenTools =>
    message?.zakekeMessageType === 'DiscoveryModeHiddenTools' && Array.isArray(message?.hiddenTools);
